/** Logical annotation font families → concrete CSS stacks.
 *
 * This is the SINGLE source of truth shared by the live Fabric editor
 * (fabric-editor.ts) and the read-only SVG overlay (annotation-overlay.ts) so a
 * stroke of text looks identical whether you're editing or reading. The KEYS
 * must stay in lock-step with the backend allowlist
 * (server/src/graphmind/domain/annotations.py ALLOWED_FONTS) and every family
 * here must actually be loaded — Inter via the @fontsource import in main.tsx,
 * the rest via web/app/index.html. */

export const DEFAULT_FONT = "marker";

/** The smallest font a text annotation renders at, in CSS pixels. Below this a
 *  run is unreadable and its caret is unusable. Both client renderers apply it.
 *
 *  It is a RENDER floor, never a storage floor. `font_size_rel` must keep the
 *  value the row holds, or a read that floors and a write that derives from the
 *  floored size ratchet the stored size up on every session. */
export const MIN_FONT_PX = 8;

/** `font_size_rel` when a row carries none. */
export const DEFAULT_FONT_SIZE_REL = 0.025;

/** Baseline-to-baseline distance, as a multiple of the font size. Fabric's IText
 *  default is 1.16 (`fabric/dist/index.mjs:4175`). The read overlay used to step
 *  its tspans by 1.25, so a run of 2 lines or more shifted the moment write mode
 *  opened. Both renderers now read this, and the editor sets it explicitly so it
 *  no longer depends on a vendor default. */
export const TEXT_LINE_HEIGHT = 1.16;

export const FONT_STACKS: Record<string, string> = {
  sans: '"Inter Variable", system-ui, sans-serif',
  serif: "Fraunces, Georgia, serif",
  mono: '"Spline Sans Mono", ui-monospace, monospace',
  marker: '"Caveat", "Comic Sans MS", cursive',
  cursive: '"Kalam", "Segoe Script", cursive',
  hand: '"Patrick Hand", "Comic Sans MS", cursive',
};

/** Short human labels for the font picker (key → label). */
export const FONT_LABELS: Record<string, string> = {
  sans: "Sans",
  serif: "Serif",
  mono: "Mono",
  marker: "Marker",
  cursive: "Cursive",
  hand: "Print",
};

const FALLBACK_STACK = '"Inter Variable", system-ui, sans-serif';

export function fontStack(key: string | null | undefined): string {
  return FONT_STACKS[key ?? DEFAULT_FONT] ?? FALLBACK_STACK;
}

export function fontLabel(key: string): string {
  return FONT_LABELS[key] ?? key;
}
